import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DailySignup } from './users-helpers';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

interface UserSignupsChartProps {
  dailySignups: DailySignup[];
}

const DAY_LABEL_FORMATTER = new Intl.DateTimeFormat('en-AU', { month: 'numeric', day: 'numeric' });

// day.date is a local "yyyy-MM-dd" bucket key (see computeDailySignups) - `new
// Date("yyyy-MM-dd")` parses as UTC midnight, which can render as the wrong
// day depending on the viewer's timezone offset, so the parts are read directly.
function formatDayLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return DAY_LABEL_FORMATTER.format(new Date(year, month - 1, day));
}

export function UserSignupsChart({ dailySignups }: UserSignupsChartProps) {
  const total = dailySignups.reduce((sum, d) => sum + d.count, 0);
  const chartData = dailySignups.map((d) => ({ label: formatDayLabel(d.date), count: d.count }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Signups (Last 7 Days)</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {total === 0 ? (
          <div className="flex h-[180px] flex-col items-center justify-center gap-1 text-center">
            <p className="text-sm font-medium text-muted-foreground">No signups in the last 7 days</p>
            <p className="text-xs text-muted-foreground">New registrations will appear here as they come in.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={40} />
              <Tooltip
                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                contentStyle={{
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  fontSize: 12,
                  color: 'hsl(var(--popover-foreground))',
                }}
                labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
              />
              <Bar dataKey="count" name="Signups" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
